import { useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getVideoTutorialInicioQuizThunk } from "../../../../store/quiz/quiz.thunks";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import "./tutorial.css";

/* ─── helpers ─── */
const getYoutubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtu\.be\/([^?]+)/,
    /youtube\.com\/embed\/([^?]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

/* ─── componente ─── */
const TutorialVideoPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { videoTutorial } = useSelector((state) => state.quiz);

  const videoRef = useRef(null);
  const hideControlsTimeout = useRef(null);

  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    dispatch(getVideoTutorialInicioQuizThunk());
  }, [dispatch]);

  /* fullscreen */
  useEffect(() => {
    const enter = async () => {
      try {
        if (!document.fullscreenElement)
          await document.documentElement.requestFullscreen();
      } catch {}
    };
    enter();
    return () => {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);

  /* autoplay para video nativo */
  useEffect(() => {
    const video = videoRef.current;
    const url =
      videoTutorial?.video_tutorial_url_generada || videoTutorial?.video_url;

    if (!video || !url || !!getYoutubeId(url)) return;

    video.muted = false;
    video.volume = 1;
    video.play().catch(() => {
      video.muted = true;
      setIsMuted(true);
      video.play().catch(() => {});
    });
    setIsPlaying(true);
  }, [videoTutorial]);

  /* eventos de progreso para video nativo */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const update = () => {
      if (!video.duration) return;
      setCurrentTime(video.currentTime);
      setDuration(video.duration);
      setProgress((video.currentTime / video.duration) * 100);
    };

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);

    video.addEventListener("timeupdate", update);
    video.addEventListener("loadedmetadata", update);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);

    return () => {
      video.removeEventListener("timeupdate", update);
      video.removeEventListener("loadedmetadata", update);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
    };
  }, []);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
    hideControlsTimeout.current = setTimeout(
      () => setShowControls(false),
      3000,
    );
  }, []);

  const togglePlay = (e) => {
    e?.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.paused ? video.play() : video.pause();
  };

  const toggleMute = (e) => {
    e?.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVolume = (e) => {
    e.stopPropagation();
    const val = Number(e.target.value);
    const video = videoRef.current;
    if (!video) return;
    video.volume = val;
    video.muted = val === 0;
    setVolume(val);
    setIsMuted(val === 0);
  };

  const handleProgressClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const video = videoRef.current;
    if (video?.duration) video.currentTime = pct * video.duration;
  };

  const handleClose = (e) => {
    e?.stopPropagation();
    navigate("/");
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    return `${Math.floor(s / 60)}:${Math.floor(s % 60)
      .toString()
      .padStart(2, "0")}`;
  };

  if (!videoTutorial?.tiene_video) return null;

  /* ── decidir qué renderizar ── */
  const rawUrl =
    videoTutorial.video_tutorial_url_generada || videoTutorial.video_url;
  const youtubeId = getYoutubeId(rawUrl);
  const isYT = !!youtubeId;

  const embedUrl = youtubeId
    ? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&showinfo=0`
    : null;

  return (
    <div
      className="tutorial-container"
      onMouseMove={handleMouseMove}
      onClick={isYT ? undefined : togglePlay}
    >
      {/* ══ YOUTUBE ══ */}
      {isYT ? (
        <div className="tutorial-yt-wrapper">
          <iframe
            className="tutorial-yt-iframe"
            src={embedUrl}
            title="Tutorial"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            frameBorder="0"
          />
        </div>
      ) : (
        /* ══ VIDEO NATIVO ══ */
        <>
          <video
            ref={videoRef}
            src={rawUrl}
            className="tutorial-video"
            playsInline
          />

          <div className="tutorial-overlay" />

          {!isPlaying && (
            <button className="center-play" onClick={togglePlay}>
              <div className="center-play-ring" />
              <Icon icon="solar:play-bold" />
            </button>
          )}

          <div className={`bottom-controls ${showControls ? "show" : "hide"}`}>
            <div className="progress-bar" onClick={handleProgressClick}>
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
              <div
                className="progress-thumb"
                style={{ left: `${progress}%` }}
              />
            </div>

            <div className="control-row">
              <div className="controls-left">
                <button onClick={togglePlay} className="ctrl-btn">
                  <Icon
                    icon={isPlaying ? "solar:pause-bold" : "solar:play-bold"}
                  />
                </button>

                <div
                  className="volume-group"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button className="ctrl-btn" onClick={toggleMute}>
                    <Icon
                      icon={
                        isMuted || volume === 0
                          ? "solar:volume-cross-bold"
                          : volume < 0.5
                            ? "solar:volume-small-bold"
                            : "solar:volume-loud-bold"
                      }
                    />
                  </button>
                  <input
                    type="range"
                    className="volume-slider"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolume}
                  />
                </div>

                <span className="time-display">
                  {fmt(currentTime)} / {fmt(duration)}
                </span>
              </div>

              <div className="controls-right">
                <span className="tutorial-label">
                  <Icon icon="solar:book-2-bold" />
                  Tutorial de inicio
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Badge y close — visibles en ambos modos */}
      <div className={`tutorial-badge ${showControls ? "show" : "hide"}`}>
        <Icon icon="solar:play-circle-bold" />
        Tutorial
      </div>

      <button
        className={`close-btn ${showControls ? "show" : "hide"}`}
        onClick={handleClose}
      >
        <Icon icon="solar:close-circle-bold" />
      </button>
    </div>
  );
};

export default TutorialVideoPage;
